import { useState } from 'react'
import { CurrencyInput } from '@/components/CurrencyInput'
import type { OperatingCostCategory } from '@/types/api'
import {
  formatCategoryTotal,
  formatOperatingCostAmount,
  formatOperatingCostDateTime,
  parseAmountInput,
} from '../lib/operating-costs'
import styles from '../CustosOperacionaisPage.module.css'
import { useWhatsAppApiCostPostings } from '../hooks/useWhatsAppApiCostPostings'

type WhatsAppApiCostPanelProps = {
  category: OperatingCostCategory
  isSaving: boolean
  onUpdateUnitRate: (categoryId: number, amount: number) => Promise<boolean>
}

export function WhatsAppApiCostPanel({
  category,
  isSaving,
  onUpdateUnitRate,
}: WhatsAppApiCostPanelProps) {
  const postings = useWhatsAppApiCostPostings(category.id)
  const [rateValue, setRateValue] = useState(
    category.unit_amount === null ? '' : String(category.unit_amount)
  )
  const [formError, setFormError] = useState<string | null>(null)

  const campaigns = postings.data?.filter_options.campaigns ?? []
  const rows = postings.data?.postings ?? []
  const meta = postings.data?.meta
  const filteredTotal = postings.data?.filtered_total ?? 0

  return (
    <article className={styles.categoryCard} aria-labelledby={`operating-cost-category-${category.id}`}>
      <header className={styles.categoryHeader}>
        <div className={styles.categoryHeading}>
          <h3 id={`operating-cost-category-${category.id}`} className={styles.categoryTitle}>
            {category.name}
          </h3>
          <span className={styles.badge}>Padrão</span>
        </div>
        <p className={styles.categoryTotal}>{formatCategoryTotal(category)}</p>
      </header>

      <p className={styles.othersCopy}>
        Defina a tarifa vigente. Cada venda paga lança automaticamente este custo, com data,
        campanha, cliente e valor da venda. Alterar a tarifa não muda lançamentos já registrados.
      </p>

      <form
        className={styles.entryForm}
        onSubmit={(event) => {
          event.preventDefault()
          const amount = parseAmountInput(rateValue)
          if (amount === null) {
            setFormError('Informe o valor em reais.')
            return
          }
          setFormError(null)
          void onUpdateUnitRate(category.id, amount).then((ok) => {
            if (ok) {
              setRateValue(String(amount))
            }
          })
        }}
      >
        <label className={styles.field} htmlFor={`whatsapp-unit-rate-${category.id}`}>
          Tarifa vigente
          <CurrencyInput
            id={`whatsapp-unit-rate-${category.id}`}
            value={rateValue}
            disabled={isSaving}
            onChange={setRateValue}
          />
        </label>
        <button type="submit" className={styles.primaryButton} disabled={isSaving}>
          {isSaving ? 'Salvando...' : 'Salvar tarifa'}
        </button>
      </form>
      {formError ? (
        <p className={styles.inlineError} role="alert">
          {formError}
        </p>
      ) : null}

      <section className={styles.postingsSection} aria-labelledby={`whatsapp-postings-${category.id}`}>
        <h4 id={`whatsapp-postings-${category.id}`} className={styles.sectionTitle}>
          Lançamentos por venda
        </h4>

        <form
          className={styles.filterBar}
          onSubmit={(event) => {
            event.preventDefault()
            const { from, to } = postings.draftFilters
            if (from !== '' && to !== '' && from > to) {
              return
            }
            postings.applyFilters()
          }}
        >
          <label className={styles.field} htmlFor={`whatsapp-filter-month-${category.id}`}>
            Mês
            <input
              id={`whatsapp-filter-month-${category.id}`}
              className={styles.input}
              type="month"
              value={postings.draftFilters.month}
              onChange={(event) =>
                postings.setDraftFilters({
                  ...postings.draftFilters,
                  month: event.target.value,
                  from: '',
                  to: '',
                })
              }
            />
          </label>
          <label className={styles.field} htmlFor={`whatsapp-filter-from-${category.id}`}>
            Data inicial
            <input
              id={`whatsapp-filter-from-${category.id}`}
              className={styles.input}
              type="date"
              value={postings.draftFilters.from}
              onChange={(event) =>
                postings.setDraftFilters({
                  ...postings.draftFilters,
                  from: event.target.value,
                  month: '',
                })
              }
            />
          </label>
          <label className={styles.field} htmlFor={`whatsapp-filter-to-${category.id}`}>
            Data final
            <input
              id={`whatsapp-filter-to-${category.id}`}
              className={styles.input}
              type="date"
              value={postings.draftFilters.to}
              onChange={(event) =>
                postings.setDraftFilters({
                  ...postings.draftFilters,
                  to: event.target.value,
                  month: '',
                })
              }
            />
          </label>
          <label className={styles.field} htmlFor={`whatsapp-filter-campaign-${category.id}`}>
            Campanha
            <select
              id={`whatsapp-filter-campaign-${category.id}`}
              className={styles.input}
              value={postings.draftFilters.campaignId}
              onChange={(event) =>
                postings.setDraftFilters({
                  ...postings.draftFilters,
                  campaignId: event.target.value,
                })
              }
            >
              <option value="">Todas as campanhas</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={String(campaign.id)}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </label>
          <div className={styles.filterActions}>
            <button type="submit" className={styles.primaryButton} disabled={postings.isLoading}>
              Filtrar
            </button>
            <button
              type="button"
              className={styles.ghostButton}
              disabled={postings.isLoading}
              onClick={postings.clearFilters}
            >
              Limpar
            </button>
          </div>
        </form>

        {postings.error ? (
          <p className={styles.inlineError} role="alert">
            {postings.error}
          </p>
        ) : null}

        {postings.draftFilters.from !== '' &&
        postings.draftFilters.to !== '' &&
        postings.draftFilters.from > postings.draftFilters.to ? (
          <p className={styles.inlineError} role="alert">
            A data final precisa ser igual ou posterior à data inicial.
          </p>
        ) : null}

        {postings.data !== null && !postings.error ? (
          <p className={styles.filteredTotal}>
            Total filtrado: {formatOperatingCostAmount(filteredTotal)}
          </p>
        ) : null}

        {postings.isLoading && postings.data === null ? (
          <p className={styles.empty}>Carregando lançamentos...</p>
        ) : postings.error ? null : rows.length === 0 ? (
          <p className={styles.empty}>Nenhum lançamento de API WhatsApp neste filtro.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th scope="col">Campanha</th>
                  <th scope="col">Cliente</th>
                  <th scope="col" className={styles.numeric}>
                    Valor da venda
                  </th>
                  <th scope="col" className={styles.numeric}>
                    Custo
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((posting) => (
                  <tr key={posting.id}>
                    <td>{formatOperatingCostDateTime(posting.posted_at)}</td>
                    <td>{posting.campaign_name}</td>
                    <td>{posting.donor_name}</td>
                    <td className={styles.numeric}>{formatOperatingCostAmount(posting.sale_amount)}</td>
                    <td className={styles.numeric}>{formatOperatingCostAmount(posting.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {meta && meta.last_page > 1 && !postings.error ? (
          <div className={styles.pagination}>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={postings.isLoading || meta.current_page <= 1}
              onClick={() => postings.goToPage(meta.current_page - 1)}
            >
              Anterior
            </button>
            <p className={styles.paginationStatus}>
              Página {meta.current_page} de {meta.last_page}
            </p>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={postings.isLoading || meta.current_page >= meta.last_page}
              onClick={() => postings.goToPage(meta.current_page + 1)}
            >
              Próxima
            </button>
          </div>
        ) : null}
      </section>
    </article>
  )
}
